// IDEA: adjust fullscreen video player's time accordingly when going fullscreen

import React, { useState, useRef, useMemo } from "react"
import styled from "styled-components"
import { em } from "polished"
import { GatsbyImage } from "gatsby-plugin-image"
import { v4 as uuidv4 } from "uuid"
import { debounce } from "lodash"
import Hls from "hls.js"

import Carousel from "../carousel"
import Modal from "../modal"
import { ReactComponent as SvgExpand } from "../../assets/icons/expand.svg"
import useRefWithCallback from "../../hooks/use-ref-with-callback"

const pauseVideos = (node) => {
  node.querySelectorAll(`video`).forEach((v) => v?.pause())

  node.querySelectorAll(`iframe`).forEach((i) => {
    i?.contentWindow.postMessage(
      JSON.stringify({
        event: `command`,
        func: `pauseVideo`,
      }),
      `*`
    )
    i?.contentWindow.postMessage(
      JSON.stringify({
        method: `pause`,
        value: ``,
      }),
      `*`
    )
  })
}

const initVideoHls = (node) => {
  node.querySelectorAll(`video`).forEach((video) => {
    if (
      !video.__hlsInited__ &&
      !video.canPlayType(`application/vnd.apple.mpegurl`) &&
      Hls.isSupported()
    ) {
      const hls = new Hls()
      hls.loadSource(video.src)
      hls.attachMedia(video)
      video.__hlsInited__ = true
    }

    // video.preload = `auto`
  })
}

const ImagesCarousel = ({
  data,
  title = ``,
  fullscreenReady = true,
  translations,
}) => {
  if (!data || !data.length) return null

  const [modalOpen, setModalOpen] = useState(false)
  const [externalConsent, setExternalConsent] = useState(false)
  const carouselProviderRef = useRef()
  const domId = useRef(`a${uuidv4()}`)

  const [carouselRef, setCarouselRef] = useRefWithCallback((node) => {
    initVideoHls(node)
  })

  const [modalCarouselRef, setModalCarouselRef] = useRefWithCallback((node) => {
    initVideoHls(node)
  })

  const [, setModalCarouselProviderRef] = useRefWithCallback((node) => {
    node.carouselStore.setStoreState({
      disableAnimation: true,
      currentSlide:
        carouselProviderRef.current.carouselStore.state.currentSlide,
    })

    window.setTimeout(
      () => node.carouselStore.setStoreState({ disableAnimation: false }),
      100
    )
  })

  const modalCloseRequest = () => setModalOpen(false)
  const externalConsentClick = () => setExternalConsent(true)
  const toggleClick = () => {
    setModalOpen(true)
    if (carouselRef.current) pauseVideos(carouselRef.current)
  }

  const carouselChange = debounce(
    () => {
      if (modalCarouselRef.current) pauseVideos(modalCarouselRef.current)
      else if (carouselRef.current) pauseVideos(carouselRef.current)
    },
    [500],
    { leading: true, trailing: false }
  )

  const iframeLoad = (e) =>
    e.currentTarget.contentWindow
      ? e.currentTarget.contentWindow.focus()
      : e.currentTarget.focus()

  const slides = useMemo(
    () =>
      data.map((image) => {
        const description = [
          image.description,
          image.copyright && `© ${image.copyright}`,
        ]
          .filter(Boolean)
          .join(`.<br>`)

        const imageDomId = `${domId.current}-${image.id}`

        return (
          <React.Fragment key={image.id}>
            {image.mediaType == `image` && (
              <Figure>
                <GatsbyImage image={image} alt={image.description || title} />

                {description && (
                  <figcaption
                    dangerouslySetInnerHTML={{ __html: description }}
                  />
                )}
              </Figure>
            )}

            {image.mediaType == `video` && (
              <Figure>
                <video
                  src={image.url}
                  preload="metadata"
                  controls
                  muted
                  loop
                  autoPlay
                  playsInline
                />

                {description && (
                  <figcaption
                    dangerouslySetInnerHTML={{ __html: description }}
                  />
                )}
              </Figure>
            )}

            {image.mediaType == `external` && (
              <>
                {externalConsent ? (
                  <Figure>
                    <iframe
                      src={image.url}
                      type="text/html"
                      title="Video"
                      loading="lazy"
                      tabIndex="0"
                      allow="autoplay; fullscreen; accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      onLoad={iframeLoad}
                    />

                    {description && (
                      <figcaption
                        dangerouslySetInnerHTML={{ __html: description }}
                      />
                    )}
                  </Figure>
                ) : (
                  <ExternalConsent role="dialog" aria-labelledby={imageDomId}>
                    <p id={imageDomId}>
                      {translations.externalMediaConsent.text.replace(
                        `{{provider}}`,
                        image.url.includes(`youtube`) ? `YouTube` : `Vimeo`
                      )}
                    </p>

                    <button type="button" onClick={externalConsentClick}>
                      <strong>
                        {translations.externalMediaConsent.button}
                      </strong>
                    </button>
                  </ExternalConsent>
                )}
              </>
            )}
          </React.Fragment>
        )
      }),
    [data, externalConsent]
  )

  return (
    <>
      {fullscreenReady && (
        <Modal isOpen={modalOpen} onRequestClose={modalCloseRequest}>
          <ModalContainer>
            <Container $inModal={true}>
              <Carousel
                slides={slides}
                slideGap={0}
                innerRef={setModalCarouselRef}
                providerRef={setModalCarouselProviderRef}
                onChange={carouselChange}
              />
            </Container>
          </ModalContainer>
        </Modal>
      )}

      <Container>
        {fullscreenReady && (
          <Toggle
            onClick={toggleClick}
            title={translations.global.enterFullscreen}
          >
            <SvgExpand aria-label={translations.global.enterFullscreen} />
          </Toggle>
        )}

        <Carousel
          slides={slides}
          slideGap={0}
          innerRef={setCarouselRef}
          providerRef={carouselProviderRef}
          insetFocus={true}
          onChange={carouselChange}
        />
      </Container>
    </>
  )
}

export default ImagesCarousel

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  position: relative;
  background-color: ${({ theme, $inModal }) =>
    (!$inModal && theme?.components?.imagesCarousel?.backgroundColor) ||
    `#000`};

  .carousel__slider {
    outline-offset: ${em(-10)};
  }

  button.carousel__back-button,
  button.carousel__next-button {
    transform: scale(0.8) translateY(-50%);
  }

  button.carousel__back-button {
    left: ${em(20)};

    @media ${({ theme }) => theme.mq.mediumDown} {
      left: ${em(10)};
    }
  }

  button.carousel__next-button {
    right: ${em(20)};

    @media ${({ theme }) => theme.mq.mediumDown} {
      right: ${em(10)};
    }
  }
`

const ModalContainer = styled.div`
  width: 100vw;
  height: 100vh;
  background-color: ${({ theme }) => theme.colors.black};
`

const Toggle = styled.button.attrs({ type: `button` })`
  width: ${em(42)};
  height: ${em(42)};
  position: absolute;
  z-index: 2;
  top: ${em(20)};
  right: ${em(20)};
  padding: ${em(10)};
  border-radius: 999em;
  background-color: ${({ theme }) => theme.colors.white};
  opacity: 0.8;

  @media ${({ theme }) => theme.mq.largeDown} {
    top: ${em(14)};
    right: ${em(14)};
    font-size: ${em(14)};
  }

  &:hover {
    opacity: 1;
  }

  &:active {
    opacity: 0.5;
  }

  &::before {
    content: "";
    position: absolute;
    top: ${em(-6)};
    bottom: ${em(-6)};
    left: ${em(-6)};
    right: ${em(-6)};
  }

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }
`

const Figure = styled.figure`
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  .gatsby-image-wrapper,
  video,
  iframe {
    width: 100%;
    height: 100%;
    position: absolute;
    z-index: 1;
    top: 0;
    bottom: 0;

    ${ModalContainer} & {
      width: calc(100% - ${em(28)});
      height: calc(100% - ${em(28)});
      top: ${em(14)};
      bottom: ${em(14)};
    }
  }

  .gatsby-image-wrapper {
    ${ModalContainer} & {
      * {
        width: 100% !important;
        max-width: 100% !important;
        height: 100% !important;
        object-fit: contain !important;
      }
    }
  }

  video {
    object-fit: cover;

    ${ModalContainer} & {
      object-fit: contain;
    }
  }

  figcaption {
    max-width: 80%;
    padding: ${em(6)} ${em(12)};
    position: absolute;
    z-index: 2;
    bottom: ${em(30)};
    right: 0;
    font-size: ${em(13)};
    color: ${({ theme }) => theme.colors.white};
    background-color: rgba(0, 0, 0, 0.7);
  }
`

const ExternalConsent = styled.div`
  width: 100%;
  height: 100%;
  padding: ${em(20)} ${em(80)};
  display: inline-flex;
  flex-direction: column;
  /* justify-content: center safe; */
  align-items: center;
  gap: ${em(20)};
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  text-align: center;
  color: ${({ theme }) => theme.colors.white};
  background-color: ${({ theme }) => theme.colors.black};

  > *:first-child {
    margin-top: auto;
  }

  > *:last-child {
    margin-bottom: auto;
  }

  p {
    max-width: 64ch;
    font-size: ${em(15)};

    @media ${({ theme }) => theme.mq.largeDown} {
      font-size: ${em(14)};
    }
  }

  button {
    text-decoration-line: underline;

    @media ${({ theme }) => theme.mq.largeDown} {
      font-size: ${em(15)};
    }
  }
`
